import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import feedApi from '../api/feedApi';
import { Profile, LoadingStatus } from '../../types';


interface FeedFilters {
  province?: string;
  city?: string;
  town?: string;
  village?: string;
  gender?: string;
  relationshipGoals?: string;
  ethnicity?: string;
  languages?: string; // comma-separated
  interests?: string; // comma-separated
  ageMin?: number;
  ageMax?: number;
}

interface FeedState {
  items: Profile[];
  cursor: string | null;
  hasMore: boolean;
  hotties: Profile[];
  showcase: Profile[];
  filters: FeedFilters;
  status: LoadingStatus;
  error: string | null;
}

const initialState: FeedState = {
  items: [],
  cursor: null,
  hasMore: true,
  hotties: [],
  showcase: [],
  filters: {},
  status: 'idle',
  error: null,
};

export const fetchFeed = createAsyncThunk('feed/fetch', async (_, { getState, rejectWithValue }) => {
  try {
    const state = getState() as { feed: FeedState };
    const res = await feedApi.getFeed({ ...state.feed.filters, limit: 20 });
    return res.data;
  } catch (err: unknown) {
    const e = err as { response?: { data?: { message?: string } } };
    return rejectWithValue(e.response?.data?.message || 'Failed to load feed');
  }
});

export const fetchMoreFeed = createAsyncThunk('feed/fetchMore', async (_, { getState, rejectWithValue }) => {
  try {
    const state = getState() as { feed: FeedState };
    const res = await feedApi.getFeed({
      ...state.feed.filters,
      cursor: state.feed.cursor || '',
      limit: 20,
    });
    return res.data;
  } catch (err: unknown) {
    const e = err as { response?: { data?: { message?: string } } };
    return rejectWithValue(e.response?.data?.message || 'Failed to load more');
  }
});

export const fetchShowcaseProfiles = createAsyncThunk('feed/showcase', async (_, { rejectWithValue }) => {
  try {
    const res = await feedApi.getShowcaseProfiles();
    return res.data as Profile[];
  } catch {
    return rejectWithValue('Failed to load showcase profiles');
  }
});

export const fetchHotties = createAsyncThunk('feed/hotties', async (_, { rejectWithValue }) => {
  try {
    const res = await feedApi.getHotties();
    return res.data as Profile[];
  } catch (err: unknown) {
    const e = err as { response?: { data?: { message?: string } } };
    return rejectWithValue(e.response?.data?.message || 'Failed to fetch hotties');
  }
});

const feedSlice = createSlice({
  name: 'feed',
  initialState,
  reducers: {
    setFilters(state, action: PayloadAction<FeedFilters>) {
      state.filters = action.payload;
      state.items = [];
      state.cursor = null;
      state.hasMore = true;
    },
    resetFeed(state) {
      state.items = [];
      state.cursor = null;
      state.hasMore = true;
      state.status = 'idle';
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchFeed.pending, (state) => { state.status = 'loading'; state.error = null; })
      .addCase(fetchFeed.fulfilled, (state, { payload }) => {
        state.status = 'succeeded';
        state.items = payload.profiles;
        state.cursor = payload.meta.cursor;
        state.hasMore = payload.meta.hasMore;
      })
      .addCase(fetchFeed.rejected, (state, { payload }) => {
        state.status = 'failed';
        state.error = payload as string;
      })
      .addCase(fetchMoreFeed.fulfilled, (state, { payload }) => {
        state.items = [...state.items, ...payload.profiles];
        state.cursor = payload.meta.cursor;
        state.hasMore = payload.meta.hasMore;
      })
      .addCase(fetchHotties.fulfilled, (state, { payload }) => {
        state.hotties = payload;
      })
      .addCase(fetchShowcaseProfiles.fulfilled, (state, { payload }) => {
        state.showcase = payload;
      });
  },
});

export const { setFilters, resetFeed } = feedSlice.actions;
export default feedSlice.reducer;

export const selectFeedItems = (state: { feed: FeedState }) => state.feed.items;
export const selectShowcaseProfiles = (state: { feed: FeedState }) => state.feed.showcase;
export const selectHasMore = (state: { feed: FeedState }) => state.feed.hasMore;
export const selectFeedStatus = (state: { feed: FeedState }) => state.feed.status;
export const selectHotties = (state: { feed: FeedState }) => state.feed.hotties;
export const selectFeedFilters = (state: { feed: FeedState }) => state.feed.filters;
export const selectFeedError = (state: { feed: FeedState }) => state.feed.error;
